import { useEffect, useState, useMemo, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { FaInstagram, FaFacebook, FaPhone, FaEnvelope, FaTrophy, FaCalendarAlt, FaUserFriends } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";

const API_BASE_URL = "https://warfc.com/api";

/* =========================================================
   UI COMPONENTS
========================================================= */

const StatCard = ({ label, value, subValue }) => (
  <div className="bg-white/5 border-l-2 border-[#d4af37] p-4 backdrop-blur-sm hover:bg-white/10 transition-all group">
    <p className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-bold">{label}</p>
    <div className="flex items-baseline gap-2">
      <p className="text-2xl font-black text-white group-hover:text-[#d4af37] transition-colors">
        {value || "N/A"}
      </p>
      {subValue && value && <span className="text-xs text-gray-500">{subValue}</span>}
    </div>
  </div>
);

const SocialIcon = ({ type, link }) => {
  if (!link || typeof link !== "string") return null;
  const Icon = type === "Instagram" ? FaInstagram : FaFacebook;
  const href = link.startsWith("http") ? link : `https://${type.toLowerCase()}.com/${link}`;
  
  return (
    <a 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="p-3 rounded-full bg-white/5 border border-white/10 hover:border-[#d4af37] hover:text-[#d4af37] transition-all text-xl"
    >
      <Icon />
    </a>
  );
};

const copyProfileLink = () => {
  navigator.clipboard.writeText(window.location.href);
  alert("Profile link copied to clipboard!");
};


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function PublicFighterProfile() {
  const { id } = useParams();
  const { user: currentUser } = useContext(AuthContext);

  const [fighter, setFighter] = useState(null);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rankingClass, setRankingClass] = useState("N"); 
  const [fights, setFights] = useState([]);

  /* 1. PRIMARY DATA FETCHING */
useEffect(() => {
  if (!id) {
    setError("Invalid Fighter ID");
    setLoading(false);
    return;
  }

  const fetchData = async () => {
    try {
      const config = {};
      
      // CRITICAL FIX: Ensure token is a valid string AND not "undefined"/"null"
      const token = currentUser?.token;
      if (token && typeof token === 'string' && token !== "undefined" && token !== "null") {
        config.headers = { Authorization: `Bearer ${token}` };
      }

      const res = await axios.get(`${API_BASE_URL}/fighters/${id}`, config);
      
      const fighterData = res.data;
      setFighter(fighterData);

      if (fighterData.club_id) {
        // Clubs are usually public, no token needed
        const clubRes = await axios.get(`${API_BASE_URL}/clubs/${fighterData.club_id}`);
        setClub(clubRes.data);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      // If it's a 403, it means the backend is still rejecting the token header
      setError(err.response?.status === 403 
        ? "Session error. Please logout and try again." 
        : (err.response?.data?.error || "Failed to load fighter")
      );
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [id, currentUser?.token]);

  /* 2. DERIVED VALUES (Defined before use in Ranking Effect) */
  const age = useMemo(() => {
    if (!fighter?.dob) return "N/A";
    const dob = new Date(fighter.dob);
    const today = new Date();
    let a = today.getFullYear() - dob.getFullYear();
    if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) {
      a--;
    }
    return a;
  }, [fighter?.dob]);

  const fighterImage = useMemo(() => {
    const imgPath = fighter?.image_large || fighter?.image_url || fighter?.image;
    return imgPath 
      ? `${API_BASE_URL}${imgPath}` 
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(fighter?.name || "Fighter")}&background=111&color=d4af37&size=512`;
  }, [fighter]);

  /* 3. RANKING DATA FETCHING */
  useEffect(() => {
    const fetchRanking = async () => {
      if (!fighter?.fighter_id) return;

      try {
        const isYouth = age !== "N/A" && age < 18; 
        const res = await axios.get(`${API_BASE_URL}/rankings`, {
          params: {
            gender: fighter.gender?.toLowerCase(),
            category: isYouth ? "youth" : "senior"
          }
        });

        const rankings = res.data;
        const f = rankings.find(r => String(r.fighter_id) === String(fighter.fighter_id));

        if (f) {
          const normalizeClass = (value) => {
            if (!value) return null;
            const v = String(value).toLowerCase();
            if (v.includes("c")) return "C";
            if (v.includes("b")) return "B";
            if (v.includes("a")) return "A";
            return null;
          };

          const normalizedCls = normalizeClass(f.class_division);
          let finalCls = "N";
          if (normalizedCls) {
            finalCls = normalizedCls;
          } else if (f.rank) {
            const r = Number(f.rank);
            finalCls = r >= 1 && r <= 3 ? "A" : r >= 4 && r <= 7 ? "B" : "C";
          }

          setRankingClass(finalCls);
          setFighter(prev => ({ ...prev, current_rank: f.rank, points: f.points }));
        }
      } catch (err) {
        console.error("Error fetching ranking:", err);
      }
    };

    fetchRanking();
  }, [fighter?.fighter_id, fighter?.gender, age]);

useEffect(() => {
  const fetchFights = async () => {
    if (!fighter?.fighter_id) return;

    try {
      const res = await axios.get(`${API_BASE_URL}/events/fights`, {
        params: { fighter_id: fighter.fighter_id }
      });

      // Handle wrapper or direct array
      const fightsArray = res.data.fights || res.data || [];
      setFights(fightsArray);
      console.log("Fights loaded:", fightsArray);
    } catch (err) {
      console.error("Error loading fights:", err);
    }
  };

  fetchFights();
}, [fighter?.fighter_id]);

const handleImageUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  if (!file.type.startsWith("image/")) {
    alert("Please upload an image file");
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    alert("Image must be under 5MB");
    return;
  }

  if (!fighter?.fighter_id) return;

  try {
    const data = new FormData();
    data.append("image", file);

    if (!fighter?.fighter_id) return;

await axios.put(
  `${API_BASE_URL}/fighters/${fighter.fighter_id}/image`,
      data,
      {
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      }
    );

    setFighter(prev => ({
      ...prev,
      image_large: URL.createObjectURL(file)
    }));

  } catch (err) {
    console.error(err);
    alert("Image upload failed");
  }
};

const { pastFights, upcomingFights } = useMemo(() => {
  const now = new Date();

  const past = [];
  const upcoming = [];

  fights.forEach(f => {
    const eventDate = new Date(f.event_date);

    if (eventDate >= now) upcoming.push(f);
    else past.push(f);
  });

  return { pastFights: past, upcomingFights: upcoming };
}, [fights]);

const getOpponent = (fight) => fight.red_fighter_id === fighter?.fighter_id ? fight.blue_name : fight.red_name;

const getResult = (fight) => {
  // If fight hasn't happened yet
  if (!fight.winner_id || fight.status !== "finished") return "Scheduled";

  // If fighter won
  if (fight.winner_id === fighter?.fighter_id) return "Win";

  // Fighter lost
  return "Loss";
};

  const canViewPrivateInfo = currentUser?.role === "admin" || currentUser?.fighter_id === fighter?.fighter_id;

  const clubLogo = club?.logo_url
    ? `${API_BASE_URL}/uploads/clubs/${club.logo_url}`
    : `${API_BASE_URL}/uploads/clubs/default-club.png`;

  /* 3. RENDER LOGIC */
  if (loading) return (
    <div className="h-screen bg-black flex flex-col items-center justify-center text-[#d4af37]">
      <div className="w-16 h-16 border-4 border-t-transparent border-[#d4af37] rounded-full animate-spin mb-4"></div>
      <p className="font-black tracking-widest uppercase">Initializing Warrior...</p>
    </div>
  );

  if (error) return (
    <div className="h-screen bg-black flex items-center justify-center p-6">
      <div className="bg-red-900/20 border border-red-500 p-8 rounded-xl text-center">
        <h2 className="text-2xl font-bold text-red-500 mb-2">Access Denied</h2>
        <p className="text-white">{error}</p>
        <Link to="/" className="mt-4 inline-block text-[#d4af37] underline">Return Home</Link>
      </div>
    </div>
  );

  if (!fighter) return null;

  return (
    <div className="bg-[#050505] text-white min-h-screen font-sans selection:bg-[#d4af37] selection:text-black">
      
      {/* STYLE INJECTOR FOR STROKE TEXT */}
      <style>{`
        .stroke-text { -webkit-text-stroke: 1px rgba(255,255,255,0.2); }
        @media (min-width: 768px) { .stroke-text { -webkit-text-stroke: 2px rgba(255,255,255,0.2); } }
      `}</style>

      {/* HERO SECTION - SCROLLING GRADIENT TEXT */}
      <section className="relative h-[80vh] md:h-[95vh] w-full overflow-hidden flex items-start bg-[#050505]">
        
        {/* --- STYLES FOR ANIMATION --- */}
        <style>{`
/* THE IMAGE GLITCH: Violent & High-Voltage */

@keyframes logo-float {
  0%, 100% { transform: rotate(-12deg) scale(1.25); }
  50% { transform: rotate(-10deg) scale(1.3); }
}
.animate-logo-float { animation: logo-float 20s ease-in-out infinite; }

/* THE NAME PULSE: Rhythmic & Heavy */
@keyframes name-power-pulse {
  0%, 100% { 
    transform: scale(1); 
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.6); 
  }
  50% { 
    transform: scale(1.04); 
    text-shadow: 0 0 50px rgba(255, 255, 255, 0.9), 0 0 80px rgba(255, 255, 255, 0.3); 
  }
}

.black-void-static {
  color: #000000; /* Pure Black Fill */
  /* Remove the stroke to prevent whitening */
  -webkit-text-stroke: 0px; 
  /* Use a sharp drop-shadow to create a white "halo" behind the black */
  filter: drop-shadow(0 0 1px rgba(255,255,255,0.8)) 
          drop-shadow(0 0 12px rgba(255,255,255,0.3));
  text-transform: uppercase;
  letter-spacing: -0.05em;
  opacity: 1; /* Keep it 100% to ensure it's black */
}

/* Ensure the White First Name has enough glow to pop */
.white-power-pulse {
  color: #FFFFFF;
  animation: name-power-pulse 2s infinite ease-in-out;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.7);
}
          @keyframes mesh {
            0%, 100% { transform: scale(1) translate(0%, 0%); }
            50% { transform: scale(1.2) translate(5%, 2%); }
          }
          @keyframes scrollText {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          .animate-mesh { animation: mesh 10s ease-in-out infinite; }
          .animate-scroll { animation: scrollText 20s linear infinite; }
          .grain { pointer-events: none; position: absolute; inset: 0; opacity: 0.1; background-image: url("https://grainy-gradients.vercel.app/noise.svg"); }
        `}</style>

        {/* --- BACKGROUND GRADIENT ENGINE --- */}
<div className="absolute inset-0 z-0">
  <div className="absolute inset-0 bg-[#0a001a]" />
  
  {/* Cyan & Pink Glows stay here... */}
  <div className="absolute top-[-10%] left-[-10%] w-[70%] h-[70%] bg-cyan-500/30 blur-[120px] rounded-full animate-mesh" />
  <div className="absolute top-[-10%] right-[-10%] w-[70%] h-[70%] bg-pink-600/30 blur-[120px] rounded-full animate-mesh" style={{ animationDelay: '-5s' }} />

  {/* --- BRANDED CLUB CREST (TOP OFFSET) --- */}
{club && (
  <div className="absolute inset-0 flex justify-center items-start overflow-hidden pointer-events-none z-10">
    <div className="relative h-full w-full max-w-7xl">
      <img 
        src={clubLogo} 
        alt="" 
        className="absolute 
          /* Position: Top Right behind shoulder */
          top-[7%] right-[5%] md:right-[10%] 
          /* Size: Strong and clear */
          w-[180px] h-[180px] md:w-[350px] md:h-[350px] 
          /* Style: Round, Sharp, and High Contrast */
          object-contain rounded-full 
          opacity-70 contrast-125 saturate-150
          drop-shadow-[0_0_40px_rgba(0,0,0,0.9)] 
          select-none"
        style={{ 
          /* Ensuring it stays sharp but fits the dark aesthetic */
          filter: 'brightness(1.1) contrast(1.2)' 
        }}
      />
      
      {/* Optional: A second faint glow behind the logo to make it "pop" */}
      <div className="absolute top-[7%] right-[5%] md:right-[10%] w-[180px] h-[180px] md:w-[350px] md:h-[350px] bg-white/5 blur-[60px] rounded-full" />
    </div>
  </div>
)}

          {/* SCROLLING BACKGROUND TEXT (First Name) */}
<div className="absolute top-[25%] left-0 w-[200%] whitespace-nowrap pointer-events-none select-none z-0 opacity-30">
  <h2 className="animate-scroll text-[30vw] font-black uppercase tracking-tighter text-white leading-none inline-block pr-20">
    {fighter.name} • {fighter.name} • {fighter.name} •
  </h2>
</div>
          {/* National Flag (Mixed with Gradients) */}
          {fighter.nationality && (
            <div className="absolute inset-0 flex items-center justify-center mix-blend-overlay opacity-15 overflow-hidden">
               <img 
                src={`https://flagcdn.com/w1280/${fighter.nationality.substring(0, 2).toLowerCase()}.png`}
                alt={fighter.nationality}
                className="w-full h-full object-cover filter blur-[2px]"
              />
            </div>
          )}
          
          <div className="grain" />
        </div>

        {/* --- THE FIGHTER (PINNED TOP) --- */}
<div className="absolute inset-0 z-20 flex justify-center items-start overflow-hidden">
  <div className="relative h-full flex justify-center items-start">
    {/* Subtle Halo behind the Head */}
    <div className="absolute top-0 w-[60%] h-[40%] bg-white/10 blur-[100px] rounded-full z-0" />
    
    {/* THE RESTORED GLITCH IMAGE */}
    <img
  src={fighterImage}
  alt={fighter.name}
  className="h-full w-auto object-contain object-top drop-shadow-[0_0_50px_rgba(0,0,0,0.8)] relative z-10 transition-transform duration-700"
/>
  </div>
</div>

{/* REMOVED: The side record (Wins/Losses) is gone to keep it cinematic */}

{/* --- FOREGROUND CONTENT --- */}
<div className="relative z-40 w-full max-w-7xl mx-auto px-6 mt-auto pb-10 md:pb-20">
  <div className="flex flex-col items-start gap-0">
    
    {/* Badge Info */}
    <div className="flex items-center gap-3 mb-6">
      <div className="bg-gradient-to-r from-cyan-500 to-pink-500 p-[1px] rounded-full">
        <div className="bg-black rounded-full px-4 py-1">
          <span className="text-white text-[10px] font-black uppercase tracking-widest">
            {fighter.nationality} • {fighter.fighting_style}
          </span>
        </div>
      </div>
    </div>
    
    {/* Primary Name Display - PURE BLACK VOID STYLE */}
<div className="flex flex-col select-none mt-4">
  <h1 className="flex flex-col">
    {/* FIRST NAME: PULSING WHITE */}
    <span className="text-5xl md:text-[6.5rem] lg:text-[8.5rem] font-black uppercase leading-[0.85] tracking-tighter white-power-pulse italic block">
      {fighter.name}
    </span>

    {/* SURNAME: PURE BLACK (Underneath) */}
    <span className="text-4xl md:text-[5rem] lg:text-[7rem] font-black uppercase leading-[0.8] tracking-[-0.05em] black-void-static block -mt-1 md:-mt-3 ml-1">
      {fighter.surname}
    </span>
  </h1>

  {/* Nickname with Electric Accents */}
  {fighter.nickname && (
    <div className="mt-4 flex items-center gap-3 relative z-30 ml-2">
      <div className="h-[2px] w-6 md:w-12 bg-cyan-500 shadow-[0_0_10px_#00ffff]" />
      <p className="text-xl md:text-4xl font-black italic text-white uppercase drop-shadow-[0_0_15px_rgba(255,255,255,0.7)]">
        "{fighter.nickname}"
      </p>
      <div className="h-[2px] w-6 md:w-12 bg-pink-500 shadow-[0_0_10px_#ff00ff]" />
    </div>
  )}
</div>
  </div>
</div>

{/* BOTTOM VIGNETTE */}
<div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-black via-transparent to-transparent z-30 pointer-events-none" />

{/* ADMIN ICON */}
{currentUser?.role === "admin" && (
  <div className="absolute top-6 left-6 z-50">
    <Link to={`/admin/fighters/${fighter.fighter_id}/edit`} className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-black hover:bg-cyan-400 transition-all shadow-xl">
       <FaTrophy className="text-lg" />
    </Link>
  </div>
)}
</section>
      {/* MAIN CONTENT GRID */}
      <div className="max-w-7xl mx-auto px-6 -mt-8 relative z-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* SIDEBAR: BIO & CLUB */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-[#111] border border-white/10 p-6 rounded-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-[#d4af37]/5 rounded-bl-full transition-all group-hover:scale-150" />
              <h3 className="text-[#d4af37] font-black uppercase tracking-wider mb-4 text-xs">Biography</h3>
              <p className="text-gray-400 text-sm leading-relaxed italic">
                {fighter.bio || "This fighter's legacy is currently being written in the fires of competition."}
              </p>
              <div className="flex gap-4 mt-8">
  <SocialIcon type="Instagram" link={fighter.instagram_url} />
  <SocialIcon type="Facebook" link={fighter.facebook_url} />

  <button
    onClick={copyProfileLink}
    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-[#d4af37] hover:text-black transition-all text-[10px] font-black uppercase"
  >
    Share
  </button>
</div>
            </div>

            {club && (
              <Link to={`/clubs/${club.club_id}`} className="group block bg-[#d4af37] p-5 rounded-xl hover:bg-white transition-all duration-300">
                <div className="flex items-center gap-4">
                  <img src={clubLogo} className="w-12 h-12 rounded-full border-2 border-black/10 shadow-lg" alt="Club" />
                  <div>
                    <p className="text-black/50 text-[10px] font-black uppercase tracking-tighter">dojo</p>
                    <p className="text-black font-black text-lg leading-tight">{club.club_name}</p>
                  </div>
                </div>
              </Link>
            )}
          </div>

          {/* MAIN STATS */}
          <div className="lg:col-span-3">
            <div className="bg-[#111] border border-white/5 p-6 md:p-10 rounded-2xl shadow-2xl">
              <div className="flex items-center gap-4 mb-10">
                <div className="h-1 w-12 bg-[#d4af37]" />
                <h2 className="text-2xl font-black uppercase tracking-widest">Tale of the Tape</h2>
              </div>
     {/* FIGHT RECORD */}
<div className="mt-12 bg-[#111] border border-white/5 p-6 md:p-10 rounded-2xl shadow-2xl">
  
  <div className="flex items-center gap-4 mb-10">
    <div className="h-1 w-12 bg-[#d4af37]" />
    <h2 className="text-2xl font-black uppercase tracking-widest">Fight Record</h2>
  </div>

  {/* UPCOMING FIGHTS */}
  {upcomingFights.length > 0 && (
    <div className="mb-10">
      <h3 className="text-[#d4af37] text-xs font-black uppercase tracking-widest mb-4">
        Upcoming
      </h3>

      <div className="space-y-3">
        {upcomingFights.map(fight => (
          <div
            key={fight.fight_id}
            className="flex justify-between items-center bg-black/40 border border-white/10 p-4 rounded-lg"
          >
            <div>
              <p className="font-bold text-white">
                vs {getOpponent(fight)}
              </p>
              <p className="text-xs text-gray-400">
                {fight.event_name}
              </p>
            </div>

            <div className="text-right">
              <p className="text-sm text-gray-300">
                {new Date(fight.event_date).toLocaleDateString()}
              </p>
              <span className="text-[10px] text-yellow-500 uppercase font-bold">
                Scheduled
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )}

  {/* PAST FIGHTS */}
  {pastFights.length > 0 && (
    <div>
      <h3 className="text-[#d4af37] text-xs font-black uppercase tracking-widest mb-4">
        Past Fights
      </h3>

      <div className="space-y-3">
        {pastFights.map(fight => {
          const result = getResult(fight);

          return (
            <div
              key={fight.fight_id}
              className="flex justify-between items-center bg-black/40 border border-white/10 p-4 rounded-lg"
            >
              <div>
                <p className="font-bold text-white">
                  vs {getOpponent(fight)}
                </p>
                <p className="text-xs text-gray-400">
                  {fight.event_name}
                </p>
              </div>

              <div className="text-right">
                <p className="text-sm text-gray-300">
                  {new Date(fight.event_date).toLocaleDateString()}
                </p>

                <span
                  className={`text-xs font-black ${
                    result === "Win"
                      ? "text-green-400"
                      : result === "Loss"
                      ? "text-red-400"
                      : "text-gray-400"
                  }`}
                >
                  {result}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )}

</div>         
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="bg-white/5 border-l-2 border-[#d4af37] p-4 backdrop-blur-sm relative overflow-hidden group">
  <p className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-bold">Class</p>
  <div className="flex items-center gap-2">
    <p className={`text-3xl font-black italic ${
      rankingClass === 'A' ? 'text-yellow-500' : 
      rankingClass === 'B' ? 'text-gray-300' : 'text-orange-400'
    }`}>
      {rankingClass}
    </p>
    {rankingClass === 'A' && <span className="text-[8px] animate-pulse bg-yellow-500/20 text-yellow-500 px-1 rounded">ELITE</span>}
  </div>
</div>
                <StatCard label="Rank" value={fighter.current_rank} />
                <StatCard label="Height" value={fighter.height} subValue="CM" />
                <StatCard label="Weight" value={fighter.actual_weight} subValue="KG" />
                <StatCard label="Age" value={age} />
                <StatCard label="Nation" value={fighter.nationality} />
                <StatCard label="Style" value={fighter.fighting_style} />
                <StatCard label="Gender" value={fighter.gender} />
                <StatCard label="Trainer" value={club?.trainer_name} />
              </div>

              {canViewPrivateInfo && (fighter.phone_number || fighter.email) && (
                <div className="mt-12 pt-8 border-t border-white/10">
                  <p className="text-[#d4af37] text-[10px] font-black uppercase mb-4 tracking-[0.2em]">Restricted Contact Info</p>
                  <div className="grid md:grid-cols-2 gap-4">
                     {fighter.phone_number && (
                       <div className="flex items-center gap-4 bg-black/40 p-4 rounded-lg border border-white/5">
                          <FaPhone className="text-[#d4af37] text-sm" />
                          <span className="text-sm font-mono tracking-tighter">{fighter.phone_number}</span>
                       </div>
                     )}
                     {fighter.email && (
                       <div className="flex items-center gap-4 bg-black/40 p-4 rounded-lg border border-white/5">
                          <FaEnvelope className="text-[#d4af37] text-sm" />
                          <span className="text-sm font-mono tracking-tighter">{fighter.email}</span>
                       </div>
                     )}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}